//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
import pluralize from "pluralize";
import { colors, greys } from "@clockwise/web-commons/src/styles/color.styles";
// smart-hold-sync imports
import { FOCUS_TIME_IDEAL_MINUTES_DEFAULT } from "./SmartHoldSync.util";
import { IContainer, IProps, IState, TFreeBusy, TReminders } from "./SmartHoldSync.d";
import { smartHoldSyncFragments } from "./SmartHoldSync.gql";
import { styles } from "./SmartHoldSync.styles";
import { updateSmartHoldSyncSettings } from "@mutations";
import { snackBarOpen } from "@actions/snack-bar.actions";
// state
import { IReduxState } from "@reducers/root.reducer";
import {
  setRequireSaveFocusTimeSync,
  setRequireSaveLunchSync,
  setRequireSaveTravelTimeSync,
} from "@actions/settings.actions";
// util
import { track, TrackingEvents } from "@util/analytics.util";
// material-ui
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  RadioGroup,
  Radio,
  Tooltip,
} from "@material-ui/core";
import { CheckCircle, RadioButtonUnchecked, InfoOutlined } from "@material-ui/icons";
import withStyles from "@material-ui/core/styles/withStyles";
import { isOnDoNotDefaultEnableFocusTimeSync } from "@util/experiment.util";

//////////////////
// COMPONENT
//////////////////
class SmartHoldSyncCmpt extends React.Component<IProps, IState> {
  private saveTimeout: ReturnType<typeof setTimeout>;

  constructor(props: IProps) {
    super(props);

    let freeBusy: TFreeBusy = null;
    let syncAll: boolean | null = null;
    let idealMinutesPerWeek: number | null = null;
    let remindersEnabled: boolean | null = null;

    // default to focus time
    let sync = false;
    let smartHoldLabel = "Smart Hold";
    if (props.smartHoldType === "FocusTime") {
      smartHoldLabel = "Focus Time";
      if (props.useDefaultValues) {
        sync = !isOnDoNotDefaultEnableFocusTimeSync();
        syncAll = false;
      } else {
        if (
          props.org.shadowCalendarSettingsErrorable &&
          props.org.shadowCalendarSettingsErrorable.__typename === "ShadowCalendarSettings"
        ) {
          if (props.org.shadowCalendarSettingsErrorable.focusTimeSyncEnabled) {
            sync = true;
          }
          syncAll = props.org.shadowCalendarSettingsErrorable.focusTimeSyncAll;
          idealMinutesPerWeek =
            props.org.shadowCalendarSettingsErrorable.focusTimeIdealMinutesPerWeek;
          remindersEnabled = props.org.shadowCalendarSettingsErrorable.focusTimeRemindersEnabled;
          if (props.org.shadowCalendarSettingsErrorable.focusTimeExternalTransparency) {
            freeBusy = props.org.shadowCalendarSettingsErrorable.focusTimeExternalTransparency;
          }
        }
      }
    }

    // lunch
    if (props.smartHoldType === "Lunch") {
      smartHoldLabel = "lunch";
      if (props.useDefaultValues) {
        sync = true;
      } else {
        if (
          props.org.shadowCalendarSettingsErrorable &&
          props.org.shadowCalendarSettingsErrorable.__typename === "ShadowCalendarSettings"
        ) {
          if (props.org.shadowCalendarSettingsErrorable.lunchSyncEnabled) {
            sync = true;
          }
          remindersEnabled = props.org.shadowCalendarSettingsErrorable.lunchRemindersEnabled;
        }
      }
    }

    // travel time
    if (props.smartHoldType === "TravelTime") {
      smartHoldLabel = "travel time";
      if (props.useDefaultValues) {
        sync = true;
      } else {
        if (
          props.org.shadowCalendarSettingsErrorable &&
          props.org.shadowCalendarSettingsErrorable.__typename === "ShadowCalendarSettings"
        ) {
          if (props.org.shadowCalendarSettingsErrorable.travelTimeSyncEnabled) {
            sync = true;
          }
          remindersEnabled = props.org.shadowCalendarSettingsErrorable.travelTimeRemindersEnabled;
        }
      }
    }

    this.state = {
      smartHoldLabel,
      freeBusy,
      syncAll,
      idealMinutesPerWeek,
      remindersEnabled,
      sync: !!sync,
    };
  }

  public componentWillUnmount() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
  }

  private handleToggle = () => {
    const { smartHoldType } = this.props;
    const { sync } = this.state;
    const newSyncValue = !sync;

    // when we toggle on focus time sync, default sync all to off
    const newSyncAllValue = smartHoldType === "FocusTime" && newSyncValue ? false : undefined;

    this.handleSyncChange(newSyncValue, newSyncAllValue);
  };

  private handleButton = (syncValue: boolean, syncAllValue?: boolean) => {
    const { sync, syncAll } = this.state;
    if (sync !== syncValue || (syncAllValue !== undefined && syncAll !== syncAllValue)) {
      this.handleSyncChange(syncValue, syncAllValue);
    }
  };

  private handleSyncChange = (newSyncValue: boolean, newSyncAllValue?: boolean) => {
    const { isOnboarding, smartHoldType } = this.props;

    track(TrackingEvents.SETTINGS.UPDATE_SMART_HOLD_SYNC, {
      isOnboarding,
      sync: newSyncValue,
      type: smartHoldType,
      syncAll: newSyncAllValue,
    });

    let state: any = { sync: newSyncValue };
    if (newSyncAllValue !== undefined) {
      state = { ...state, syncAll: newSyncAllValue };
    }

    this.setState(state);

    if (this.props.onChange) {
      this.props.onChange(newSyncValue, newSyncAllValue);
    }

    // begin save process
    this.beginSave();
  };

  private handleFreeBusyUpdate = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const freeBusyValue = e.target.value as TFreeBusy;
    const { isOnboarding, smartHoldType } = this.props;
    track(TrackingEvents.SETTINGS.UPDATE_SMART_HOLD_FREE_BUSY, {
      isOnboarding,
      freeBusy: freeBusyValue,
      type: smartHoldType,
    });
    this.setState({
      freeBusy: freeBusyValue,
    });

    // begin save process
    this.beginSave();
  };

  private handleRemindersUpdate = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const remindersEnabledValue = (e.target.value as TReminders) === "on";
    const { isOnboarding, smartHoldType } = this.props;
    track(TrackingEvents.SETTINGS.UPDATE_SMART_HOLD_REMINDERS, {
      isOnboarding,
      remindersEnabled: remindersEnabledValue,
      type: smartHoldType,
    });
    this.setState({
      remindersEnabled: remindersEnabledValue,
    });

    // begin save process
    this.beginSave();
  };

  private handleSyncAllChangeString = (_e: React.ChangeEvent<{}>, syncAll: string): void => {
    return this.handleSyncAllChange(syncAll === "true");
  };

  private handleSyncAllChange = (syncAll: boolean): void => {
    const { isOnboarding, smartHoldType } = this.props;
    track(TrackingEvents.SETTINGS.UPDATE_SMART_HOLD_SYNC_ALL, {
      isOnboarding,
      syncAll,
      type: smartHoldType,
    });
    this.setState({
      syncAll,
    });

    // begin save process
    this.beginSave();
  };

  private handleThresholdChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const idealMinutesPerWeek = parseInt(e.target.value) || FOCUS_TIME_IDEAL_MINUTES_DEFAULT;
    const { isOnboarding, smartHoldType } = this.props;
    track(TrackingEvents.SETTINGS.UPDATE_SMART_HOLD_IDEAL_MINUTES_PER_WEEK, {
      isOnboarding,
      idealMinutesPerWeek,
      type: smartHoldType,
    });
    this.setState({
      idealMinutesPerWeek,
    });

    // begin save process
    this.beginSave();
  };

  private onSuccess = () => {
    const { isOnboarding } = this.props;
    const { smartHoldLabel } = this.state;
    if (!isOnboarding) {
      this.props.snackBarOpen(
        `Your ${smartHoldLabel} settings were updated. It may take a few minutes for changes to take effect.`,
      );
    }
    this.clearRequireSave();
  };

  private onFailure = () => {
    const { smartHoldLabel } = this.state;
    this.props.snackBarOpen(`Sorry, there was a problem updating your ${smartHoldLabel} settings`);
    this.clearRequireSave();
  };

  private clearRequireSave = () => {
    const { smartHoldType } = this.props;

    // update redux
    if (smartHoldType === "FocusTime") {
      this.props.setRequireSaveFocusTimeSync(false);
    } else if (smartHoldType === "Lunch") {
      this.props.setRequireSaveLunchSync(false);
    } else if (smartHoldType === "TravelTime") {
      this.props.setRequireSaveTravelTimeSync(false);
    }
  };

  private beginSave = () => {
    const { smartHoldType } = this.props;

    // if its not a manual save, kick off the save delay and other redux state
    if (this.props.allowAutoSave) {
      // update required save
      if (smartHoldType === "FocusTime") {
        this.props.setRequireSaveFocusTimeSync(true);
      } else if (smartHoldType === "Lunch") {
        this.props.setRequireSaveLunchSync(true);
      } else if (smartHoldType === "TravelTime") {
        this.props.setRequireSaveTravelTimeSync(true);
      }

      // create the save timeout
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
      }
      this.saveTimeout = setTimeout(() => {
        this.doSave();
      }, 5000);
    }
  };

  private doSave = () => {
    const { org, relay, smartHoldType, isOnboarding } = this.props;
    const { sync, freeBusy, syncAll, idealMinutesPerWeek, remindersEnabled } = this.state;

    let input: ISchema.IUpdateSmartHoldSyncSettingsMutationInput = { orgRelayId: org.id };
    if (smartHoldType === "FocusTime") {
      input = { orgRelayId: org.id, focusTimeSyncEnabled: sync };
      if (sync && freeBusy) {
        const focusTimeExternalTransparency = freeBusy as ISchema.FocusTimeExternalTransparency;
        input = { ...input, focusTimeExternalTransparency };
      }
      if (sync && syncAll !== null) {
        input = { ...input, focusTimeSyncAll: syncAll };
      }
      if (sync && idealMinutesPerWeek !== null) {
        input = { ...input, focusTimeIdealMinutesPerWeek: idealMinutesPerWeek };
      }
      if (sync && remindersEnabled !== null) {
        input = { ...input, focusTimeRemindersEnabled: remindersEnabled };
      }
    } else if (smartHoldType === "Lunch") {
      input = { orgRelayId: org.id, lunchSyncEnabled: sync };
      if (sync && remindersEnabled !== null) {
        input = { ...input, lunchRemindersEnabled: remindersEnabled };
      }
    } else if (smartHoldType === "TravelTime") {
      input = { orgRelayId: org.id, travelTimeSyncEnabled: sync };
      if (sync && remindersEnabled !== null) {
        input = { ...input, travelTimeRemindersEnabled: remindersEnabled };
      }
    }
    track(TrackingEvents.SETTINGS.SAVE_SMART_HOLD_SYNC, {
      sync,
      freeBusy,
      isOnboarding,
      idealMinutesPerWeek,
      remindersEnabled,
      syncAll,
      type: this.props.smartHoldType,
    });
    updateSmartHoldSyncSettings(relay.environment, input, this.onSuccess, this.onFailure);

    if (this.props.onSave) {
      this.props.onSave(sync);
    }
  };

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const { smartHoldType } = this.props;

    // check for triggered save
    if (
      smartHoldType === "FocusTime" &&
      nextProps.triggerSaveFocusTimeSyncMillis > this.props.triggerSaveFocusTimeSyncMillis
    ) {
      this.doSave();
    }
    if (
      smartHoldType === "Lunch" &&
      nextProps.triggerSaveLunchSyncMillis > this.props.triggerSaveLunchSyncMillis
    ) {
      this.doSave();
    }
    if (
      smartHoldType === "TravelTime" &&
      nextProps.triggerSaveTravelTimeSyncMillis > this.props.triggerSaveTravelTimeSyncMillis
    ) {
      this.doSave();
    }
  }

  private renderTypicalHours = () => {
    const { org, smartHoldType, classes } = this.props;
    if (smartHoldType !== "FocusTime") {
      return null;
    }
    if (org.focusTimeStats.__typename === "GraphEntityError") {
      return null;
    }

    const hours = Math.round(org.focusTimeStats.minutesPerWeek / 60);

    return (
      <div className={classes.typicalText}>
        You typically have{" "}
        <span style={{ fontWeight: 600 }}>
          {hours} {pluralize("hour", hours)}
        </span>{" "}
        per week
      </div>
    );
  };

  private renderSingleLine = () => {
    const { smartHoldType } = this.props;

    if (smartHoldType !== "FocusTime") {
      return "";
    }

    return this.renderThresholdLabel();
  };

  private renderButtons = () => {
    const {
      classes,
      buttonImageOn,
      buttonImageOff,
      buttonContainerStyle,
      buttonStyle,
      buttonTextStyle,
      smartHoldType,
    } = this.props;
    const { sync, smartHoldLabel } = this.state;

    if (smartHoldType == "FocusTime") {
      return this.renderFocusTimeThresholdButtons();
    }

    return (
      <div className={classes.buttonContainer} style={buttonContainerStyle}>
        <div
          className={sync ? classes.buttonControl : classes.buttonControlInactive}
          style={buttonStyle}
          onClick={() => this.handleButton(true)}
        >
          <div
            className={sync ? classes.buttonText : classes.buttonTextInactive}
            style={buttonTextStyle}
          >
            {sync ? (
              <CheckCircle
                className={classes.buttonIcon}
                style={{ color: colors.blueSky.darker }}
              />
            ) : (
              <RadioButtonUnchecked
                className={classes.buttonIconInactive}
                style={{ color: greys.slate }}
              />
            )}
            Yes, show {smartHoldLabel}
          </div>
          {buttonImageOn ? (
            <div className={sync ? classes.buttonImage : classes.buttonImageInactive}>
              <img src={buttonImageOn} />
            </div>
          ) : null}
        </div>
        <div
          className={!sync ? classes.buttonControl : classes.buttonControlInactive}
          style={buttonStyle}
          onClick={() => this.handleButton(false)}
        >
          <div
            className={!sync ? classes.buttonText : classes.buttonTextInactive}
            style={buttonTextStyle}
          >
            {!sync ? (
              <CheckCircle
                className={classes.buttonIcon}
                style={{ color: colors.blueSky.darker }}
              />
            ) : (
              <RadioButtonUnchecked
                className={classes.buttonIconInactive}
                style={{ color: greys.slate }}
              />
            )}
            No, do not show {smartHoldLabel} events
          </div>
          {buttonImageOff ? (
            <div className={!sync ? classes.buttonImage : classes.buttonImageInactive}>
              <img src={buttonImageOff} />
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  private renderFocusTimeThresholdButtons = () => {
    const {
      classes,
      buttonImageOn,
      buttonImageOff,
      buttonContainerStyle,
      buttonStyle,
      buttonTextStyle,
      disableFocusTimeScheduleAll,
    } = this.props;
    const { sync, smartHoldLabel, syncAll } = this.state;

    const all = sync && syncAll;
    const some = sync && !syncAll;
    const none = !sync;

    return (
      <div className={classes.buttonContainer} style={buttonContainerStyle}>
        <div className={classes.buttonThresholdControls}>
          {!disableFocusTimeScheduleAll && (
            <div
              className={
                all ? classes.buttonThresholdControl : classes.buttonThresholdControlInactive
              }
              style={buttonStyle}
              onClick={() => this.handleButton(true, true)}
            >
              <div
                className={all ? classes.buttonThresholdText : classes.buttonThresholdTextInactive}
                style={buttonTextStyle}
              >
                {all ? (
                  <CheckCircle
                    className={classes.buttonIcon}
                    style={{ color: colors.blueSky.darker }}
                  />
                ) : (
                  <RadioButtonUnchecked
                    className={classes.buttonIconInactive}
                    style={{ color: greys.slate }}
                  />
                )}
                <div>
                  <div>Schedule all my available {smartHoldLabel}</div>
                  {this.renderTypicalHours()}
                </div>
              </div>
            </div>
          )}

          <div
            className={
              some ? classes.buttonThresholdControl : classes.buttonThresholdControlInactive
            }
            style={buttonStyle}
            onClick={() => this.handleButton(true, false)}
          >
            <div
              className={some ? classes.buttonThresholdText : classes.buttonThresholdTextInactive}
              style={buttonTextStyle}
            >
              {some ? (
                <CheckCircle
                  className={classes.buttonIcon}
                  style={{ color: colors.blueSky.darker }}
                />
              ) : (
                <RadioButtonUnchecked
                  className={classes.buttonIconInactive}
                  style={{ color: greys.slate }}
                />
              )}
              {this.renderThresholdLabel()}
            </div>
          </div>
          <div
            className={
              none ? classes.buttonThresholdControl : classes.buttonThresholdControlInactive
            }
            style={buttonStyle}
            onClick={() => this.handleButton(false)}
          >
            <div
              className={none ? classes.buttonThresholdText : classes.buttonThresholdTextInactive}
              style={buttonTextStyle}
            >
              {none ? (
                <CheckCircle
                  className={classes.buttonIcon}
                  style={{ color: colors.blueSky.darker }}
                />
              ) : (
                <RadioButtonUnchecked
                  className={classes.buttonIconInactive}
                  style={{ color: greys.slate }}
                />
              )}
              Don't schedule any {smartHoldLabel} for me
            </div>
          </div>
        </div>
        <div className={classes.buttonThresholdImage}>
          {sync ? (
            <img src={buttonImageOn} style={{ width: 188 }} />
          ) : (
            <img src={buttonImageOff} style={{ width: 188 }} />
          )}
        </div>
      </div>
    );
  };

  private renderToggle = () => {
    const { classes, recommendEnable, disabled } = this.props;
    const { sync, smartHoldLabel } = this.state;

    const toggleText = `Automatically schedule ${smartHoldLabel} events on my calendar ${
      recommendEnable ? " (recommended)" : ""
    }`;

    return (
      <div className={!disabled ? classes.toggleContainer : classes.toggleDisabledContainer}>
        <div className={classes.toggleControl}>
          <FormControl>
            <FormControlLabel
              control={<Switch color="primary" checked={sync} />}
              onChange={this.handleToggle}
              label={toggleText}
              labelPlacement="end"
              classes={{ label: classes.toggleText }}
            />
          </FormControl>
        </div>
      </div>
    );
  };

  private renderThreshold = () => {
    const { classes } = this.props;
    const { sync, syncAll } = this.state;

    const ftSyncAll = syncAll === null || !sync ? "" : syncAll.toString();

    return (
      <div style={!sync ? { cursor: "not-allowed" } : undefined}>
        <div className={sync ? classes.thresholdContainer : classes.thresholdDisabledContainer}>
          <RadioGroup
            name="focusTimeSyncaAll"
            value={ftSyncAll}
            onChange={this.handleSyncAllChangeString}
            style={styles.thresholdRadioContainer}
          >
            <FormControlLabel
              control={<Radio color="primary" />}
              value="true"
              label={
                <div>
                  <div>Schedule all my available Focus Time</div>
                  <div style={{ paddingBottom: 10 }}>{this.renderTypicalHours()}</div>
                </div>
              }
              classes={{ label: classes.thresholdRadioText }}
            />
            <FormControlLabel
              control={<Radio color="primary" />}
              value="false"
              label={this.renderThresholdLabel()}
              classes={{ label: classes.thresholdRadioText }}
            />
          </RadioGroup>
        </div>
      </div>
    );
  };

  private renderThresholdLabel = () => {
    const { classes, isOnboarding } = this.props;
    const { idealMinutesPerWeek } = this.state;

    const ftThreshold = idealMinutesPerWeek
      ? idealMinutesPerWeek
      : FOCUS_TIME_IDEAL_MINUTES_DEFAULT;
    let tooltipText = `Clockwise will try to schedule around ${
      ftThreshold / 60
    } hours of Focus Time per week at times that are convenient for you.`;
    if (isOnboarding) {
      tooltipText += " You can manage your Focus Time preferences in Settings.";
    }

    return (
      <div className={classes.thresholdSelectContainer}>
        Schedule&nbsp;
        <FormControl style={styles.thresholdSelectFormControl}>
          <Select
            name="focusTimeThreshold"
            value={ftThreshold}
            onChange={this.handleThresholdChange}
          >
            <MenuItem value={120}>2 hours</MenuItem>
            <MenuItem value={180}>3 hours</MenuItem>
            <MenuItem value={240}>4 hours</MenuItem>
            <MenuItem value={300}>5 hours</MenuItem>
            <MenuItem value={360}>6 hours</MenuItem>
            <MenuItem value={420}>7 hours</MenuItem>
            <MenuItem value={480}>8 hours</MenuItem>
            <MenuItem value={540}>9 hours</MenuItem>
            <MenuItem value={600}>10 hours</MenuItem>
            <MenuItem value={660}>11 hours</MenuItem>
            <MenuItem value={720}>12 hours</MenuItem>
            <MenuItem value={780}>13 hours</MenuItem>
            <MenuItem value={840}>14 hours</MenuItem>
            <MenuItem value={900}>15 hours</MenuItem>
            <MenuItem value={960}>16 hours</MenuItem>
            <MenuItem value={1020}>17 hours</MenuItem>
            <MenuItem value={1080}>18 hours</MenuItem>
            <MenuItem value={1140}>19 hours</MenuItem>
            <MenuItem value={1200}>20 hours</MenuItem>
            <MenuItem value={1260}>21 hours</MenuItem>
            <MenuItem value={1320}>22 hours</MenuItem>
            <MenuItem value={1380}>23 hours</MenuItem>
            <MenuItem value={1440}>24 hours</MenuItem>
            <MenuItem value={1500}>25 hours</MenuItem>
            <MenuItem value={1560}>26 hours</MenuItem>
            <MenuItem value={1620}>27 hours</MenuItem>
            <MenuItem value={1680}>28 hours</MenuItem>
            <MenuItem value={1740}>29 hours</MenuItem>
            <MenuItem value={1800}>30 hours</MenuItem>
            <MenuItem value={1860}>31 hours</MenuItem>
            <MenuItem value={1920}>32 hours</MenuItem>
            <MenuItem value={1980}>33 hours</MenuItem>
            <MenuItem value={2040}>34 hours</MenuItem>
            <MenuItem value={2100}>35 hours</MenuItem>
            <MenuItem value={2160}>36 hours</MenuItem>
            <MenuItem value={2220}>37 hours</MenuItem>
            <MenuItem value={2280}>38 hours</MenuItem>
            <MenuItem value={2340}>39 hours</MenuItem>
            <MenuItem value={2400}>40 hours</MenuItem>
          </Select>
        </FormControl>
        &nbsp;Focus Time per week
        <Tooltip placement="top" title={tooltipText} classes={{ popper: classes.infoIconPopper }}>
          <InfoOutlined className={classes.infoIcon} />
        </Tooltip>
      </div>
    );
  };

  private renderFreeBusy = () => {
    const { classes } = this.props;
    const { freeBusy, sync } = this.state;
    return (
      <div style={!sync ? { cursor: "not-allowed" } : undefined}>
        <div className={sync ? classes.freeBusyContainer : classes.freeBusyDisabledContainer}>
          <FormControl fullWidth>
            <InputLabel shrink htmlFor="cw-focus-time-availability-select">
              Availability during Focus Time events
            </InputLabel>
            <Select
              value={freeBusy}
              onChange={this.handleFreeBusyUpdate}
              disabled={!sync}
              name="Availability during Focus Time events"
              inputProps={{
                name: "Availability during Focus Time events",
                id: "cw-focus-time-availability-select",
              }}
            >
              <MenuItem key={"busy"} value={"Opaque"}>
                Busy, unavailable to meet
              </MenuItem>
              <MenuItem key={"free"} value={"Transparent"}>
                Free, others can schedule with me
              </MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>
    );
  };

  private renderReminders = () => {
    const { classes } = this.props;
    const { remindersEnabled, sync, smartHoldLabel } = this.state;

    return (
      <div style={!sync ? { cursor: "not-allowed" } : undefined}>
        <div className={sync ? classes.remindersContainer : classes.remindersDisabledContainer}>
          <FormControl fullWidth>
            <InputLabel shrink htmlFor={`cw-${smartHoldLabel}-reminders-select`}>
              Event notifications for {smartHoldLabel}
            </InputLabel>
            <Select
              value={!!remindersEnabled ? "on" : "off"}
              onChange={this.handleRemindersUpdate}
              disabled={!sync}
              name={`Event notifications for ${smartHoldLabel}`}
              inputProps={{
                name: `${smartHoldLabel} event notifications`,
                id: `cw-${smartHoldLabel}-reminders-select`,
              }}
            >
              <MenuItem key={"on"} value={"on"}>
                Use Google Calendar defaults
              </MenuItem>
              <MenuItem key={"off"} value={"off"}>
                Off
              </MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>
    );
  };

  public render() {
    const { renderMode, smartHoldType } = this.props;
    if (renderMode === "SingleLine") {
      return this.renderSingleLine();
    } else if (renderMode === "Buttons") {
      return this.renderButtons();
    } else {
      return (
        <div>
          {this.renderToggle()}
          {smartHoldType === "FocusTime" ? (
            <div>
              {this.renderThreshold()}
              {this.renderFreeBusy()}
            </div>
          ) : null}
          {this.renderReminders()}
        </div>
      );
    }
  }
}

//////////////////
// WITH STYLES
//////////////////
export const SmartHoldSyncStyled = withStyles(styles)(SmartHoldSyncCmpt);

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    triggerSaveFocusTimeSyncMillis: state.settings.triggerSaveFocusTimeSyncMillis,
    triggerSaveLunchSyncMillis: state.settings.triggerSaveLunchSyncMillis,
    triggerSaveTravelTimeSyncMillis: state.settings.triggerSaveTravelTimeSyncMillis,
  };
}

const SmartHoldSyncRedux = connect(mapStateToProps, {
  snackBarOpen,
  setRequireSaveFocusTimeSync,
  setRequireSaveLunchSync,
  setRequireSaveTravelTimeSync,
})(SmartHoldSyncStyled);

//////////////////
// RELAY
//////////////////
export const SmartHoldSync = createFragmentContainer<IContainer>(
  SmartHoldSyncRedux,
  smartHoldSyncFragments,
);
